// Copyright (c) Docugami, Inc. All rights reserved.**/

import { ContextualMenuItemType, DefaultButton, IButtonProps, IContextualMenuItem, IContextualMenuItemStyles, Stack } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { aaTextColor, emptyColor, emptyGray, greyText, primaryColorDark, primaryTheme, primaryThemeSettings, secondaryTheme } from "components/shell/presentation/AppStyles";
import { WorkPathCategory, getFilterTypesForWorkPathCategory, typeToInfoMap, workPathCategoryToDisplayInfo } from "components/work_path/WorkPathsHelper";
import { WorkPathsViewType, WorkPathsViewer } from "components/work_path/WorkPathsViewer";
import { useFeatureFlag } from "hooks/FeatureFlags";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { merge } from "lodash";
import { ClusterInfo } from "networking/api";
import { WorkPathType } from "networking/WorkPathApi";
import { useCallback, useMemo } from "react";
import { FeatureFlags } from "store/featureFlags/types";
import { useGetWorkPathsQuery } from "store/work_path/workPathSlice";
import styled from "styled-components";
import { CreateWorkPathSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { ExplorerView } from "./ExplorerView";

const ProjectsTitle = styled.span`
    font-size: 24px;
    font-family: ${dgFontFamily};
    font-weight: 600;
    color: ${primaryThemeSettings.palette.black};
    white-space: nowrap;
`;

interface DocSetWorkPathExplorerViewProps {
    docSet: ClusterInfo;
    className?: string;
}

export const DocSetWorkPathExplorerView = (props: DocSetWorkPathExplorerViewProps) => {
    const navigationService = useNavigationService();
    const demoFeaturesEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);

    const { isLoading } = useGetWorkPathsQuery();

    const onWorkPathMenuItemClick = useCallback((type: WorkPathType) => () => {
        Telemetry.trackEvent<CreateWorkPathSchema>({
            workPathType: type,
            eventName: TelemetryEvent.CreateWorkPath
        });
        navigationService.navToNewWorkPathPage(NavigationType.PushNewPage, type, props.docSet.id);
    }, [navigationService, props.docSet.id]);

    const items: IContextualMenuItem[] = useMemo(() => {
        const menuItemSharedStyles: IContextualMenuItemStyles = {
            root: {
                height: 'initial'
            },
            linkContent: {
                padding: '5px 0',
                boxSizing: 'border-box'
            },
            label: {
                color: greyText,
                fontFamily: dgFontFamily,
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: 20,
                textWrap: 'wrap',
                lineHeight: 24,
            }
        }

        const items: IContextualMenuItem[] = [];

        // Add the menu items for creating projects
        Object.entries(workPathCategoryToDisplayInfo).forEach(([id, info]) => {
            // Add the category heading
            items.push({
                key: id,
                text: info.title,
                itemType: ContextualMenuItemType.Header
            })

            // Add the workpaths for this category
            const types = getFilterTypesForWorkPathCategory(id as WorkPathCategory, demoFeaturesEnabled);
            items.push(...types.map(type => ({
                key: type,
                text: typeToInfoMap.get(type)?.label,
                itemProps: {
                    styles: menuItemSharedStyles
                },
                onClick: onWorkPathMenuItemClick(type)
            })))
        });

        // Add the menu item for chat
        items.push({
            key: 'chat_section',
            text: 'Chat',
            itemType: ContextualMenuItemType.Header
        })

        items.push({
            key: 'chat',
            text: 'Chat with your doc set',
            itemProps: {
                styles: menuItemSharedStyles
            },
            onClick: () => navigationService.navToChatPage(NavigationType.PushNewPage, props.docSet.id)
        })

        return items;
    }, [demoFeaturesEnabled, onWorkPathMenuItemClick, navigationService, props.docSet.id])

    const header = (
        <Stack
            horizontal
            horizontalAlign="space-between"
        >
            <ProjectsTitle>Projects</ProjectsTitle>
            <DgFluentButton
                menuProps={{
                    items: items,
                    styles: {
                        root: {
                            padding: '10px 15px',
                            '.ms-ContextualMenu-divider': {
                                display: 'none'
                            },
                            maxWidth: 350
                        },
                        title: {
                            fontFamily: dgFontFamily,
                            fontStyle: 'normal',
                            fontWeight: 500,
                            fontSize: 22,

                        },
                        header: {
                            fontFamily: dgFontFamily,
                            fontStyle: 'normal',
                            fontWeight: 700,
                            fontSize: 24,
                            color: greyText,
                            textAlign: 'center',
                            borderBottom: `1px solid ${emptyGray}`,
                            margin: '10px 0'
                        }
                    }
                }}
            >
                START NEW
            </DgFluentButton>
        </Stack>
    )

    return (
        !isLoading ?
            <ExplorerView
                className={props.className}
                header={header}
                content={(
                    <WorkPathsViewer
                        docSetId={props.docSet.clusterId}
                        viewType={WorkPathsViewType.List}
                        iconHeight={10} />
                )}
                overlays={[]}
                footer={null}
                telemetryId={'DocSetWorkPathExplorerView'} />
            :
            null
    )
}

const DgFluentButton = (props: IButtonProps) => {
    return (<DefaultButton
        {...props}
        styles={merge(props.styles, {
            root: {
                color: props.disabled ? aaTextColor : primaryTheme.palette.white,
                background: props.disabled ? emptyColor : primaryTheme.palette.themePrimary,
                borderRadius: 5,
                padding: '8px 13px',
                cursor: 'pointer',
                borderWidth: 0,
            },
            rootHovered: {
                color: props.disabled ? aaTextColor : primaryTheme.palette.white,
                ':active': {
                    backgroundColor: primaryColorDark,
                },
                backgroundColor: props.disabled ? '' : secondaryTheme.palette.themeDarkAlt,
            },
            label: {
                fontFamily: dgFontFamily,
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: 16,
            },
            rootExpanded: {
                color: props.disabled ? aaTextColor : primaryTheme.palette.white,
            },
            rootExpandedHovered: {
                color: props.disabled ? aaTextColor : primaryTheme.palette.white,
            },
            rootPressed: {
                color: props.disabled ? aaTextColor : primaryTheme.palette.white,
            },
        })}
    />)
}
